// Scroll-depth (25/50/75/90 %) + rage-click tracking → GA4.
// Katsotaan mistä käyttäjät poistuvat ja missä turhautuvat. Kevyt,
// passiivinen kuuntelija — käynnistetään kerran per sivunlataus
// hydraation jälkeen (ks. src/components/Layout.tsx).
import { trackEvent } from "./gtag";

let started = false;

export function startEngagementTracking() {
  if (typeof window === "undefined") return;
  if (started) return;
  started = true;

  // --- Scroll depth ---
  const thresholds = [25, 50, 75, 90];
  const fired = new Set<number>();
  let ticking = false;

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        (document.documentElement.scrollHeight || document.body.scrollHeight) -
        window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.min(100, Math.round((scrollTop / docHeight) * 100));
      for (const t of thresholds) {
        if (pct >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent("scroll_depth", {
            percent_scrolled: t,
            page_path: window.location.pathname,
            non_interaction: true,
          });
        }
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // Reset thresholds on client-side route changes (SPA nav).
  let lastPath = window.location.pathname;
  const resetIfPathChanged = () => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      fired.clear();
    }
  };
  window.addEventListener("popstate", resetIfPathChanged);
  // Patch pushState/replaceState to detect programmatic nav.
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    const r = origPush.apply(this, args as Parameters<typeof origPush>);
    resetIfPathChanged();
    return r;
  };
  history.replaceState = function (...args) {
    const r = origReplace.apply(this, args as Parameters<typeof origReplace>);
    resetIfPathChanged();
    return r;
  };

  // --- Rage clicks: 3+ clicks in the same ~40px radius within 1s ---
  type ClickEvt = { x: number; y: number; t: number };
  const recent: ClickEvt[] = [];
  const WINDOW_MS = 1000;
  const RADIUS_PX = 40;
  const THRESHOLD = 3;

  window.addEventListener(
    "click",
    (e) => {
      const now = performance.now();
      recent.push({ x: e.clientX, y: e.clientY, t: now });
      // Prune old
      while (recent.length && now - recent[0].t > WINDOW_MS) recent.shift();
      // Count clicks near the current one
      const close = recent.filter(
        (c) => Math.hypot(c.x - e.clientX, c.y - e.clientY) <= RADIUS_PX,
      );
      if (close.length >= THRESHOLD) {
        const target = e.target as Element | null;
        const tag = target?.tagName?.toLowerCase() ?? "unknown";
        const id = (target as HTMLElement | null)?.id || undefined;
        const cls =
          typeof (target as HTMLElement | null)?.className === "string"
            ? ((target as HTMLElement).className as string).slice(0, 120)
            : undefined;
        const label =
          (target as HTMLElement | null)?.getAttribute?.("aria-label") ??
          target?.textContent?.trim().slice(0, 60);
        trackEvent("rage_click", {
          page_path: window.location.pathname,
          element_tag: tag,
          element_id: id,
          element_class: cls,
          element_label: label,
          click_count: close.length,
          non_interaction: true,
        });
        // Clear so we don't spam another event on the very next click.
        recent.length = 0;
      }
    },
    { passive: true, capture: true },
  );
}